/* @flow */

import * as React from 'react';
import { get, sortBy } from 'lodash/fp';
import glamorous from 'glamorous';
import { position } from 'polished';
import numeral from 'numeral';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import ChallengeEntryPosition from 'app/components/challenges/ChallengeEntryPosition';
import RewriteLink from 'app/components/routing/RewriteLink';
import t from 'app/utils/translate';
import { hover } from '../../utils/cssUtils';

type Props = {
  challengeId: string,
  entryId: string,
  expandType: 'edit' | 'summary' | 'view',
  isRacing: boolean,
  positions?: Object,
  rank?: number,
  selections: Array<*>,
  score?: number,
  winnings?: number
};

const defaultProps = {
  positions: {},
  rank: undefined,
  score: undefined,
  winnings: undefined
};

const Wrapper = glamorous.div(({ theme }) => ({
  backgroundColor: theme.color.basis6,
  borderColor: 'white',
  borderStyle: 'solid',
  borderWidth: '0 0 1px',
  display: 'flex',
  flexDirection: 'column',
  padding: '0.5rem 0',
  position: 'relative',
  [theme.mediaQuery.desktopAndUp]: {
    flexDirection: 'row',
    padding: '0.5rem 0 0.5rem 10px'
  }
}));

const Main = glamorous.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 1,
  flexShrink: 1,
  flexWrap: 'wrap',
  padding: '0 50px 1rem 30px',
  [theme.mediaQuery.desktopAndUp]: {
    padding: '0 1rem'
  }
}));

const Extra = glamorous.div(({ theme }) => ({
  borderTop: `1px solid ${theme.color.basis5}`,
  paddingTop: '0.5rem',
  paddingLeft: '2.5rem',
  [theme.mediaQuery.desktopAndUp]: {
    borderTop: 0,
    display: 'flex',
    flexBasis: 120 * 3,
    flexGrow: 0,
    flexShrink: 0,
    paddingTop: 0,
    paddingLeft: 0
  }
}));

const Item = glamorous.div(({ theme }) => ({
  flexBasis: '50%',
  flexGrow: 0,
  flexShrink: 0,
  padding: '0.5rem',
  [theme.mediaQuery.tabletPortraitAndUp]: {
    flexBasis: `${100 / 3}%`
  },
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexBasis: `${100 / 4}%`
  }
}));

const Cell = glamorous.div(({ theme }) => ({
  [theme.mediaQuery.desktopAndUp]: {
    alignItems: 'flex-start',
    border: 0,
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'flex-start',
    padding: '0.5rem 0 0 0'
  }
}));

const CellLabel = glamorous.span(({ theme }) => ({
  color: theme.color.basis1,
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    width: '80px',
    display: 'inline-block'
  },
  [theme.mediaQuery.desktopAndUp]: {
    fontWeight: theme.fontWeight.demiBold,
    fontSize: '0.78rem',
    textTransform: 'uppercase'
  }
}));

const CellText = glamorous.span(
  ({ theme }) => ({
    display: 'inline-block',
    width: '5rem',
    [theme.mediaQuery.desktopAndUp]: {
      width: 'auto'
    }
  }),
  ({ highlight, theme }) =>
    highlight
      ? {
          [theme.mediaQuery.desktopAndUp]: {
            color: theme.color.success
          }
        }
      : undefined
);

const Callout = glamorous.div(({ theme }) => ({
  ...position('absolute', 0, 0, 0, null),
  [theme.mediaQuery.desktopAndUp]: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    flexBasis: 130,
    flexGrow: 0,
    flexShrink: 0,
    padding: '0 12px',
    ...position('relative', 0)
  }
}));

const SelectButton = glamorous(RewriteLink)(
  ({ theme }) => ({
    display: 'flex',
    flexBasis: 'auto',
    color: theme.color.basis3,
    padding: '0.5em 0.75em',
    fontSize: '2rem',
    [theme.mediaQuery.desktopAndUp]: {
      backgroundColor: theme.color.basis1,
      borderRadius: '0.25em',
      minWidth: 80,
      fontSize: '0.83rem',
      padding: '0.25em 0.5em',
      textAlign: 'left',
      color: 'white',
      textTransform: 'uppercase',
      ...hover({
        backgroundColor: theme.color.basis3
      }),
      '&::before': {
        content: 'none'
      },
      '&::after': {
        content: '"❯"',
        marginLeft: 'auto'
      }
    }
  }),
  ({ icon }) => ({
    '&::before': {
      content: {
        edit: '"\\f37e"',
        summary: '"\\f133"',
        view: '"\\f133"'
      }[icon],
      fontFamily: 'Ionicons'
    }
  })
);

const DesktopLabel = glamorous.span(({ theme }) => ({
  display: 'none',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'inline'
  }
}));

function ChallengeEntry({
  challengeId,
  entryId,
  expandType,
  isRacing,
  positions,
  rank,
  selections,
  score,
  winnings
}: Props) {
  const positionAbbrs = Object.entries(positions).map(p => get('[1].abbr', p)) || [];
  const showSelections = expandType === 'edit' || expandType === 'summary' || expandType === 'view';

  return (
    <Wrapper>
      <Main>
        {showSelections &&
          !isRacing &&
          positionAbbrs.map(abbr => {
            const filteredSelections = selections.filter(s => s.abbr === abbr);
            return filteredSelections.map(pos => (
              <Item key={pos.id}>
                <ChallengeEntryPosition label={pos.abbr} entities={pos.entities} />
              </Item>
            ));
          })}
        {showSelections &&
          isRacing &&
          selections.map(({ entities }) =>
            sortBy('gameStartTime', entities).map((ent, i) => (
              <Item key={ent.id}>
                <ChallengeEntryPosition label={`L${i + 1}`} entities={[ent]} />
              </Item>
            ))
          )}
      </Main>
      {(expandType === 'summary' || expandType === 'view') && (
        <Extra>
          <Cell>
            {rank !== undefined && (
              <React.Fragment>
                <CellLabel>{t('Position')} </CellLabel>
                <CellText>#{rank}</CellText>
              </React.Fragment>
            )}
          </Cell>
          <Cell>
            {score !== undefined && (
              <React.Fragment>
                <CellLabel>{t('Points')} </CellLabel>
                <CellText>{numeral(score).format('0.00')}</CellText>
              </React.Fragment>
            )}
          </Cell>
          <Cell>
            <CellLabel>{t('Winnings')} </CellLabel>
            <CellText highlight={winnings > 0}>
              <CurrencyIcon />
              {numeral(winnings).format('0,0.00')}
            </CellText>
          </Cell>
        </Extra>
      )}
      <Callout>
        <SelectButton icon={expandType} to={`/challenge/${challengeId}/${entryId}`}>
          <DesktopLabel>{expandType === 'edit' ? 'Edit' : 'View'}</DesktopLabel>
        </SelectButton>
      </Callout>
    </Wrapper>
  );
}

ChallengeEntry.defaultProps = defaultProps;

export default ChallengeEntry;
